import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import { AppLayout } from "./components/layout";
import { MarketingLayout } from "./components/marketing";
import { AppContextProvider } from "./hooks/useAppContext";
import { SessionProvider } from "./hooks/useSession";
import { RequireAuth } from "./components/RequireAuth";
import { Landing } from "./routes/Landing";
import { Guardians } from "./routes/Guardians";
import { Features } from "./routes/Features";
import { About } from "./routes/About";
import { ContactPage } from "./routes/ContactPage";
import { Privacy, Terms } from "./routes/Legal";
import {
  AcceptableUse,
  AiTransparency,
  CookiePolicy,
  Dpa,
  RefundsAndCancellations,
  SecurityPage,
  Subprocessors,
} from "./routes/legal-pages";
import { Status } from "./routes/Status";
import { Support } from "./routes/Support";
import { Artifacts } from "./routes/Artifacts";
import { Login } from "./routes/Login";
import { Overview } from "./routes/Overview";
import { Onboarding } from "./routes/Onboarding";
import { CommunityOnboarding } from "./routes/CommunityOnboarding";
import { Moderation } from "./routes/Moderation";
import { Engagement } from "./routes/Engagement";
import { Settings } from "./routes/shells";
import { LinkedAccounts } from "./routes/LinkedAccounts";
import { WorkspaceSettings } from "./routes/WorkspaceSettings";
import { Server } from "./routes/Server";
import { Members } from "./routes/Members";
import { Blueprints } from "./routes/Blueprints";
import { BlueprintDetail } from "./routes/BlueprintDetail";
import { Snapshot } from "./routes/Snapshot";
import { DryRun } from "./routes/DryRun";
import { Simulator } from "./routes/Simulator";
import { ReleaseReview } from "./routes/ReleaseReview";
import { DeploymentProgress } from "./routes/DeploymentProgress";
import { ReleaseReport } from "./routes/ReleaseReport";
import { RollbackReview } from "./routes/RollbackReview";
import { Balder } from "./routes/Balder";
import { Integrations } from "./routes/Integrations";
import { N8nConnection } from "./routes/N8nConnection";
import { IntegrationRss } from "./routes/IntegrationRss";
import { IntegrationStreaming } from "./routes/IntegrationStreaming";
import { IntegrationGameWorkshop } from "./routes/IntegrationGameWorkshop";
import { WorkflowHistory } from "./routes/WorkflowHistory";
import { NotificationInbox } from "./routes/NotificationInbox";
import { Pricing } from "./routes/Pricing";
import { Billing } from "./routes/Billing";
import { PlanComparison } from "./routes/PlanComparison";
import { Checkout } from "./routes/Checkout";
import { Usage } from "./routes/Usage";
import { Invoices } from "./routes/Invoices";
import { LaunchReadiness } from "./routes/LaunchReadiness";
import { AccountMerges } from "./routes/AccountMerges";
import { Roles } from "./routes/Roles";
import { Tickets } from "./routes/Tickets";
import { Automations } from "./routes/Automations";
import { Content } from "./routes/Content";
import { Analytics } from "./routes/Analytics";
import { Logs } from "./routes/Logs";
import { BotConfig } from "./routes/BotConfig";

export const router = createBrowserRouter([
  {
    element: <MarketingLayout />,
    children: [
      { path: "/", element: <Landing /> },
      { path: "/features", element: <Features /> },
      { path: "/about", element: <About /> },
      { path: "/guardians", element: <Guardians /> },
      { path: "/pricing", element: <Pricing /> },
      { path: "/contact", element: <ContactPage /> },
      { path: "/privacy", element: <Privacy /> },
      { path: "/terms", element: <Terms /> },
      { path: "/security", element: <SecurityPage /> },
      { path: "/legal/cookies", element: <CookiePolicy /> },
      { path: "/legal/acceptable-use", element: <AcceptableUse /> },
      { path: "/legal/subprocessors", element: <Subprocessors /> },
      { path: "/legal/dpa", element: <Dpa /> },
      { path: "/legal/refunds-and-cancellations", element: <RefundsAndCancellations /> },
      { path: "/legal/ai-transparency", element: <AiTransparency /> },
    ],
  },
  { path: "/status", element: <Status /> },
  { path: "/login", element: <Login /> },
  {
    // Everything below is customer data. `RequireAuth` confirms the session
    // before any of it renders, which is what makes logout, direct-URL entry and
    // back-navigation behave: an unauthenticated visit lands on /login instead of
    // a dashboard shell.
    path: "/app",
    element: (
      <AppContextProvider>
        <RequireAuth>
          <AppLayout />
        </RequireAuth>
      </AppContextProvider>
    ),
    children: [
      { index: true, element: <Overview /> },
      { path: "members", element: <Members /> },
      { path: "roles", element: <Roles /> },
      { path: "tickets", element: <Tickets /> },
      { path: "automations", element: <Automations /> },
      { path: "content", element: <Content /> },
      { path: "analytics", element: <Analytics /> },
      { path: "logs", element: <Logs /> },
      { path: "bot-config", element: <BotConfig /> },
      { path: "onboarding", element: <Onboarding /> },
      { path: "community-onboarding", element: <CommunityOnboarding /> },
      { path: "moderation", element: <Moderation /> },
      { path: "engagement", element: <Engagement /> },
      { path: "servers", element: <Server /> },
      { path: "blueprints", element: <Blueprints /> },
      { path: "blueprints/:blueprintId", element: <BlueprintDetail /> },
      { path: "snapshot", element: <Snapshot /> },
      { path: "dry-run", element: <DryRun /> },
      { path: "simulator", element: <Simulator /> },
      { path: "release-review", element: <ReleaseReview /> },
      { path: "deployment", element: <DeploymentProgress /> },
      { path: "release-report", element: <ReleaseReport /> },
      { path: "rollback-review", element: <RollbackReview /> },
      { path: "balder", element: <Balder /> },
      { path: "integrations", element: <Integrations /> },
      { path: "integrations/n8n", element: <N8nConnection /> },
      { path: "integrations/rss", element: <IntegrationRss /> },
      { path: "integrations/streaming", element: <IntegrationStreaming /> },
      { path: "integrations/game", element: <IntegrationGameWorkshop /> },
      { path: "integrations/workflows", element: <WorkflowHistory /> },
      { path: "inbox", element: <NotificationInbox /> },
      { path: "billing", element: <Billing /> },
      { path: "billing/plans", element: <PlanComparison /> },
      { path: "billing/checkout", element: <Checkout /> },
      { path: "usage", element: <Usage /> },
      { path: "invoices", element: <Invoices /> },
      { path: "artifacts", element: <Artifacts /> },
      { path: "launch-readiness", element: <LaunchReadiness /> },
      // Platform-operator surface, like launch readiness: reachable by URL and from
      // Settings → Linked accounts for the handful of people whose role allows it,
      // and deliberately absent from the sidebar every customer sees. The page
      // gates on the role itself; leaving it out of NAV only avoids advertising a
      // door nobody else can open.
      { path: "account-merges", element: <AccountMerges /> },
      { path: "support", element: <Support /> },
      // /app/contact used to render a form that verified a Turnstile token and then
      // said "we've received your request" while storing nothing and sending nothing.
      // It is kept only as a redirect so existing links and bookmarks land on the
      // desk that actually records the conversation.
      { path: "contact", element: <Navigate to="/app/support" replace /> },
      { path: "settings", element: <Settings /> },
      { path: "settings/linked-accounts", element: <LinkedAccounts /> },
      { path: "settings/workspace", element: <WorkspaceSettings /> },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);

export function App(): JSX.Element {
  // One session for the whole app: fetched once, and updated everywhere the
  // instant the customer logs out.
  return (
    <SessionProvider>
      <RouterProvider router={router} />
    </SessionProvider>
  );
}
